import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Trophy, Users, Sword, CalendarCheck, ChartBar, MonitorPlay } from "@phosphor-icons/react";

const LOGO = process.env.REACT_APP_LOGO_URL;
const HERO_BG = process.env.REACT_APP_HERO_BG_URL;

const features = [
  { icon: Users, title: "CLUBES & JUGADORES", desc: "Gestiona clubes, directivos, profesores y jugadores en un solo lugar." },
  { icon: Trophy, title: "TORNEOS", desc: "Crea y administra torneos con inscripciones y brackets automaticos." },
  { icon: Sword, title: "RETAS", desc: "Desafia a otros jugadores y registra resultados en tiempo real." },
  { icon: CalendarCheck, title: "RENTA DE CANCHAS", desc: "Reserva canchas y paga en linea de forma rapida y segura." },
  { icon: ChartBar, title: "ESTADISTICAS", desc: "Puntaje, jugadas, ranking y mas en un dashboard interactivo." },
  { icon: MonitorPlay, title: "STREAMING", desc: "Transmite en vivo desde tus canchas con Synology Surveillance." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="landing-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO} alt="Logo" className="h-10 w-10 object-contain rounded-full" />
            <span className="font-['Barlow_Condensed'] font-black text-lg uppercase tracking-wider text-white">
              Disfruta y Diviertete
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login">
              <Button variant="ghost" className="text-gray-300 hover:text-[#CCFF00] uppercase text-xs tracking-widest font-semibold" data-testid="nav-login-btn">
                Ingresar
              </Button>
            </Link>
            <Link to="/register">
              <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide text-xs" data-testid="nav-register-btn">
                Registrarse
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-[#0A0A0A]/80 to-[#0A0A0A]" />
        </div>
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto animate-fade-in">
          <img src={LOGO} alt="Disfruta y Diviertete" className="h-32 w-32 mx-auto mb-8 drop-shadow-2xl rounded-full" data-testid="hero-logo" />
          <h1 className="font-['Barlow_Condensed'] text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white leading-none mb-4">
            Disfruta y <span className="text-[#CCFF00]">Diviertete</span>
          </h1>
          <p className="text-base md:text-lg text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
            La plataforma completa para gestionar tu club de padel. Torneos, retas, canchas, estadisticas y streaming en vivo.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide px-8 py-6 text-sm" data-testid="hero-register-btn">
                Comenzar Ahora
              </Button>
            </Link>
            <Link to="/login">
              <Button variant="outline" className="border-white/20 text-white hover:border-white hover:bg-white/5 font-bold uppercase tracking-wide px-8 py-6 text-sm" data-testid="hero-login-btn">
                Ya tengo cuenta
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 md:py-32 px-6" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#CCFF00] mb-3">Funcionalidades</p>
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl lg:text-4xl font-bold uppercase tracking-tight text-white mb-16">
            Todo lo que necesitas para tu club
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
            {features.map((f) => (
              <div key={f.title} className="card-tactical p-6 animate-fade-in opacity-0 group" data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <f.icon size={32} weight="duotone" className="text-[#CCFF00] mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mb-6">
            Unete a la comunidad
          </h2>
          <p className="text-gray-400 mb-8">Registrate gratis y comienza a gestionar tu club de padel hoy mismo.</p>
          <Link to="/register">
            <Button className="bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold uppercase tracking-wide px-10 py-6" data-testid="cta-register-btn">
              Crear Cuenta Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <img src={LOGO} alt="" className="h-6 w-6 rounded-full" />
            <span className="font-['Barlow_Condensed'] font-bold text-sm uppercase tracking-wider text-gray-500">Disfruta y Diviertete</span>
          </div>
          <p className="text-xs text-gray-600">Todos los derechos reservados 2025</p>
        </div>
      </footer>
    </div>
  );
}
